// Unified file extractor that automatically selects the best method based on browser capabilities

import FileSystemExtractor from './filesystem.js'
import FirefoxFolderExtractor from './firefox.js'
import BasicExtractor from './basic.js'

const UnifiedFileExtractor = {
    // Detect the best available method for the current browser
    detectBestMethod() {
        // Priority 1: Modern File System Access API (Chrome, Edge, Opera)
        if (FileSystemExtractor.isSupported()) {
            return {
                name: 'FileSystemAccess',
                extractor: FileSystemExtractor,
                supportsFolders: true
            }
        }
        
        // Priority 2: Firefox with webkitGetAsEntry
        if (FirefoxFolderExtractor.isSupported()) {
            return {
                name: 'FirefoxWebkit',
                extractor: FirefoxFolderExtractor,
                supportsFolders: true
            }
        }
        
        // Priority 3: Generic webkitGetAsEntry (Safari, older browsers)
        if ('webkitGetAsEntry' in DataTransferItem.prototype) {
            return {
                name: 'GenericWebkit',
                extractor: {
                    async extract(items) {
                        const files = []
                        const entries = []
                        
                        // Convert items to entries
                        for (let i = 0; i < items.length; i++) {
                            const item = items[i]
                            if (item.kind === 'file') {
                                const entry = item.webkitGetAsEntry()
                                if (entry) {
                                    entries.push(entry)
                                }
                            }
                        }
                        
                        // Process entries using Firefox extractor logic
                        for (const entry of entries) {
                            if (entry.isDirectory) {
                                files.push(...await FirefoxFolderExtractor.readDirectory(entry))
                            } else if (entry.isFile) {
                                const file = await FirefoxFolderExtractor.getFileFromEntry(entry)
                                if (file) {
                                    files.push(file)
                                }
                            }
                        }
                        
                        return files
                    }
                },
                supportsFolders: true
            }
        }
        
        // Fallback: Basic file extraction (no folder support)
        return {
            name: 'Basic',
            extractor: BasicExtractor,
            supportsFolders: false
        }
    },
    
    // Extract files using the best available method
    async extract(itemsOrFiles) {
        const method = this.detectBestMethod()
        
        // Log the method being used for debugging
        console.info(`Using ${method.name} extractor (folders supported: ${method.supportsFolders})`)
        
        try {
            // Handle different input types
            if (itemsOrFiles instanceof FileList) {
                // If it's a FileList and we don't support folders, use basic extractor
                if (!method.supportsFolders) {
                    return BasicExtractor.extract(itemsOrFiles)
                }
                // Otherwise, this shouldn't happen in normal drag & drop
                console.warn('FileList provided but folder support is available. This is unusual.')
                return BasicExtractor.extract(itemsOrFiles)
            }
            
            // Normal case: DataTransferItemList
            return await method.extractor.extract(itemsOrFiles)
        } catch (error) {
            console.error('Error extracting files with', method.name, error)
            
            // Fallback to basic extraction on error
            if (method.name !== 'Basic' && itemsOrFiles instanceof FileList) {
                console.info('Falling back to basic extraction')
                return BasicExtractor.extract(itemsOrFiles)
            }
            
            throw error
        }
    },
    
    // Get information about current browser support
    getBrowserSupport() {
        const method = this.detectBestMethod()
        return {
            method: method.name,
            supportsFolders: method.supportsFolders,
            browser: this.detectBrowser(),
            capabilities: {
                fileSystemAccess: 'getAsFileSystemHandle' in DataTransferItem.prototype,
                webkitEntry: 'webkitGetAsEntry' in DataTransferItem.prototype,
                firefox: navigator.userAgent.toLowerCase().includes('firefox')
            }
        }
    },
    
    // Detect browser type
    detectBrowser() {
        const ua = navigator.userAgent.toLowerCase()
        if (ua.includes('firefox')) return 'Firefox'
        if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari'
        if (ua.includes('edg')) return 'Edge'
        if (ua.includes('chrome')) return 'Chrome'
        if (ua.includes('opera')) return 'Opera'
        return 'Unknown'
    }
}

export default UnifiedFileExtractor 